import { Component, inject, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { ENV, LoggerService } from '@danangelus/ngx-app-core';
import { ThreadUtils } from "@danangelus/ngx-utils";
import { AccordionModule } from 'primeng/accordion';
import { ButtonModule } from 'primeng/button';
import { ChartModule } from 'primeng/chart';
import { DataViewModule } from 'primeng/dataview';
import { Message } from 'primeng/message';
import { ProgressSpinner } from "primeng/progressspinner";
import { Skeleton } from "primeng/skeleton";
import { SessionService } from '../../core/security/session.service';
import { AppManagerService } from '../../core/services/app-manager.service';
import { NotificationService } from '../../core/services/notification.service';
import { Application } from '../../models/application.model';
import { GatewayError } from '../../models/gateway.model';
import { Bulletin, BulletinReadership, BulletinReadStatus } from '../../models/notifications/bulletin.model';
import { PartAcc } from '../../models/part-acc.model';
import { SurveyData } from '../../models/survey-data.model';
import { User } from '../../models/user.model';
import { VehicleSale } from "../../models/vehicle-sale.model";

const log = LoggerService.get('HomeComponent')

/**
 * User dashboard displays the available applications on load.
 *
 * @author Dan Bennett
 */
@Component({
    selector: 'app-home',
    standalone: true,
    imports: [
        AccordionModule,
        ButtonModule,
        ChartModule,
        DataViewModule,
        Message,
        ProgressSpinner,
        Skeleton,
    ],
    templateUrl: './home.component.html',
    styleUrl: './home.component.sass'
})
export class HomeComponent implements OnInit {

    sessionService = inject(SessionService)
    appManager = inject(AppManagerService)
    notificationService = inject(NotificationService)
    route = inject(ActivatedRoute)

    loadingApplications = true
    loadingMetrics = true
    loadingBulletins = true
    applications: Application[] = []
    messageType = ''
    // @ts-expect-error Loaded via ngOnInit
    surveyData: SurveyData
    dealerSurveyChartData: any
    dealerSurveyChartOpts = {indexAxis: 'y'}
    partAccData: PartAcc[] = []
    dealerPartSalesChartData: any
    vehicleSaleData: any[] = []
    dealerVehicleSalesChartData: any

    dealerComms = ENV.config.get('dealer-comms-url')
    expertCode = ENV.config.get('expert-code')

    openBulletinId: string | undefined
    bulletins: BulletinReadStatus[] = []

    async ngOnInit() {
        this.loadingBulletins = true
        this.route.queryParams.subscribe(params => {
            if (Object.prototype.hasOwnProperty.call(params, 'accessDenied')) {
                this.messageType = 'ACCESS_DENIED'
            } else if (Object.prototype.hasOwnProperty.call(params, 'disabled')) {
                this.messageType = 'DISABLED'
            }
        })
        log.debug('ngOnInit', `Getting Application data for User ${this.user!.getId()}`)
        this.applications = await this.appManager.getDashboard()
        this.loadingApplications = false

        if (this.sessionService.user()!.dealerNo !== 'N/A') {
            try {
                this.prepareSurveyCharts()
                this.preparePartAccCharts()
                this.prepareVehicleSalesCharts()
                this.loadingMetrics = false
            } catch (err: any) {
                log.error(err.message)
                this.loadingMetrics = true
            }
        }

        // await ThreadUtils.sleep(5000)
        await this.getBulletins()
        this.loadingBulletins = false

        log.debug('ngOnInit', 'Loaded dashboard')
    }

    get user(): User | null {
        return this.sessionService.user()
    }

    async getBulletins() {
        this.bulletins = await this.notificationService.getBulletins()
    }

    loadApp(application: Application) {
        application.loading = true
        window.open(this.loadUrl(application))
        ThreadUtils.sleep(5000).then(() => application.loading = false)
    }

    loadUrl(application: Application) {
        return `${application.baseUrl}?expertCode=${application.expertCode}&userID=${this.sessionService.user()?.userId}&SSOTicketId=${this.sessionService.user()?.ssoTicketId}`
    }

    get isDealerMember(): boolean {
        const user = this.sessionService.user()!
        return !!user.dealerNo &&
               user.dealerNo !== 'N/A'
    }

    async markAsRead(bulletinRS: BulletinReadStatus) {
        if (this.openBulletinId) {
            const closedBulletin = this.bulletins.find(bulletin => bulletin.bulletin!.id == this.openBulletinId)!
            const readStatus = this.getReadStatus(closedBulletin)
            if (readStatus == 'unread') {
                log.debug('markAsRead', `Marking Bulletin with ID [${this.openBulletinId}] as read.`)
                const actBulletin = closedBulletin.bulletin!
                const bulletinFromDto = Bulletin.fromDto(actBulletin)

                const newReadership = new BulletinReadership()
                newReadership.bulletin = bulletinFromDto
                newReadership.userName = this.user?.getId()
                newReadership.dealer = this.user?.dealerNo
                newReadership.lastModified = new Date()

                const saveStatus = await this.notificationService.saveBulletinReadStatus(bulletinFromDto.id!, newReadership)
                if (saveStatus) {
                    closedBulletin.readership!.push(newReadership) // update front-end read status - when page refreshes it will pick up read status from back-end
                    this.openBulletinId = (this.openBulletinId === bulletinRS.bulletin!.id) ? undefined : bulletinRS.bulletin!.id

                } else {
                    this.messageType = "SAVE_STATUS_ERROR"
                    log.debug("markAsRead", `Failed to save read status for bulletin [${actBulletin.subject}] and it's read status: ${readStatus}`)
                }
            } else {
                this.openBulletinId = bulletinRS.bulletin!.id
            }

        } else {
            this.openBulletinId = bulletinRS.bulletin!.id!
            log.debug('markAsRead', `Bulletin with ID [${this.openBulletinId}] has been opened.`)
        }
    }

    /**
     * Split the bulletins by read/unread, and order both sets by publishDate (newest at the top)
     */
    get sortedBulletins() {
        const read = this.separateBulletinsByStatus(this.bulletins, 'read')
        const unread = this.separateBulletinsByStatus(this.bulletins, 'unread')

        return [...unread, ...read]
    }

    getReadStatus(bulletin: BulletinReadStatus): string {
        if (!bulletin.readership || !this.sessionService.user()) {
            return 'unread'
        }
        return bulletin.readership.some(r => r.userName === this.sessionService.user()!.getId()) ? 'read' : 'unread'
    }

    prepareSurveyCharts(): void {
        this.appManager.getSurveyMetrics().then((response) => {
            if (response instanceof GatewayError) {
                log.error(`Error received from App Manager with Error: ${response.error}, Message: ${response.message} when getting Survey data`)
            } else {
                try {
                    this.surveyData = new SurveyData(response)
                    const salesData = this.surveyData.salesSurveyData
                    const serviceData = this.surveyData.serviceSurveyData
                    if (salesData != null) {
                        this.dealerSurveyChartData = {
                            labels: [
                                'LTR - Top Box Rating (%)',
                                'Trust Rating (%)',
                                'Dealership Facilities Rating (%)',
                                'Staff Rating (%)',
                                'Ease of Service Rating (%)',
                                'Dealership Rating (Stars)'
                            ],
                            datasets: [
                                {
                                    label: 'Sales',
                                    data: [
                                        salesData.ltrTopBoxRating,
                                        salesData.trustRating,
                                        salesData.facilitiesRating,
                                        salesData.staffRating,
                                        salesData.easeOfServiceRating,
                                        salesData.dealershipRating,
                                    ]
                                },
                                {
                                    label: 'Service',
                                    data: [
                                        serviceData.ltrTopBoxRating,
                                        serviceData.trustRating,
                                        serviceData.facilitiesRating,
                                        serviceData.staffRating,
                                        serviceData.easeOfServiceRating,
                                        serviceData.dealershipRating,
                                    ]
                                }
                            ]
                        }
                    }
                } catch (err: any) {
                    log.error(`Error thrown when processing Customer Experience data: ${err}`)
                }
            }
        })
    }

    preparePartAccCharts(): void {
        this.appManager.getPartAccMetrics().then((response) => {
            if (response instanceof GatewayError) {
                log.error(`Error received from App Manager with Error: ${response.error}, Message: ${response.message} when getting Part Acc data`)
            } else {
                try {
                    this.partAccData = response
                    const dealerPartSales = this.partAccData
                        .filter((partAcc) => partAcc.procType != undefined)
                    let q1NetTotal = 0
                    let q2NetTotal = 0
                    let q3NetTotal = 0
                    let q4NetTotal = 0
                    dealerPartSales
                        .forEach((sale) => {
                            switch (sale.fiscalQuarter) {
                                case 1:
                                    q1NetTotal += sale.dealerNetTotal!
                                    break
                                case 2:
                                    q2NetTotal += sale.dealerNetTotal!
                                    break
                                case 3:
                                    q3NetTotal += sale.dealerNetTotal!
                                    break
                                default:
                                    q4NetTotal += sale.dealerNetTotal!
                            }
                        })
                    if (dealerPartSales?.length) {
                        this.dealerPartSalesChartData = {
                            labels: ['Q1', 'Q2', 'Q3', 'Q4'],
                            datasets: [
                                {
                                    label: 'Total',
                                    data: [q1NetTotal, q2NetTotal, q3NetTotal, q4NetTotal]
                                }
                            ]
                        }
                    }
                } catch (err: any) {
                    log.error(`Error thrown when processing PartAcc data: ${err}`)
                }
            }
        })
    }

    prepareVehicleSalesCharts(): void {
        this.appManager.getVehicleSalesMetrics().then((response) => {
            if (response instanceof GatewayError) {
                log.error(`Error received from App Manager with Error: ${response.error}, Message: ${response.message} when getting Vehicle Sales data`)
            } else {
                try {
                    this.vehicleSaleData = response

                    let q1Total = 0
                    let q2Total = 0
                    let q3Total = 0
                    let q4Total = 0
                    this.vehicleSaleData
                        .filter((vehicleSale: VehicleSale) => !vehicleSale.regTarget)
                        .forEach((vs: VehicleSale) => {
                            switch (vs.quarter) {
                                case 1:
                                    q1Total += vs.regCount!
                                    break
                                case 2:
                                    q2Total += vs.regCount!
                                    break
                                case 3:
                                    q3Total += vs.regCount!
                                    break
                                default:
                                    q4Total += vs.regCount!
                                    break
                            }
                        })

                    const targets = this.vehicleSaleData
                        .filter((vehicleSale: VehicleSale) => vehicleSale.regTarget)
                    const q1Target = targets.find((vs: VehicleSale) => vs.quarter == 1).regTarget
                    const q2Target = targets.find((vs: VehicleSale) => vs.quarter == 2).regTarget
                    const q3Target = targets.find((vs: VehicleSale) => vs.quarter == 3).regTarget
                    const q4Target = targets.find((vs: VehicleSale) => vs.quarter == 4).regTarget

                    this.dealerVehicleSalesChartData = {
                        labels: ['Q1', 'Q2', 'Q3', 'Q4'],
                        datasets: [
                            {
                                type: 'bar',
                                label: 'Actual',
                                data: [q1Total, q2Total, q3Total, q4Total]
                            },
                            {
                                type: 'line',
                                label: 'Target',
                                data: [q1Target, q2Target, q3Target, q4Target]
                            }
                        ]
                    }
                } catch (err: any) {
                    log.error(`Error thrown when processing Vehicle Sales data: ${err}`)
                }
            }
        })
    }

    downloadDocument(bulletinId: string, documentId: string): string {
        return `${this.dealerComms}/bulletin/${bulletinId}/document/${documentId}?SSOTicketId=${this.user?.ssoTicketId}&userID=${this.user?.getId()}&expertCode=${this.expertCode}`
    }

    downloadAttachment(bulletinId: string, attachmentId: string): string {
        return `${this.dealerComms}/bulletin/${bulletinId}/attachment/${attachmentId}?SSOTicketId=${this.user?.ssoTicketId}&userID=${this.user?.getId()}&expertCode=${this.expertCode}`
    }

    private separateBulletinsByStatus(bulletins: BulletinReadStatus[], targetStatus: string): BulletinReadStatus[] {
        return bulletins
            .filter(b => this.getReadStatus(b) === targetStatus)
            .sort((b1, b2) => b1.bulletin!.publishDate > b2.bulletin!.publishDate ? -1 : 1)
    }

    hasRole(role: string): boolean {
        return this.user?.groups?.includes(role.toUpperCase()) ?? false
    }
}
